import z from 'zod';

import { ListSuperadminActivityActorType } from '../../../api/generated/model/listSuperadminActivityActorType';
import { ListSuperadminActivityEntityType } from '../../../api/generated/model/listSuperadminActivityEntityType';

import type { ListSuperadminActivityActorType as ListSuperadminActivityActorTypeT } from '../../../api/generated/model/listSuperadminActivityActorType';
import type { ListSuperadminActivityEntityType as ListSuperadminActivityEntityTypeT } from '../../../api/generated/model/listSuperadminActivityEntityType';

const entityTypeValues = new Set(Object.values(ListSuperadminActivityEntityType));
const actorTypeValues = new Set(Object.values(ListSuperadminActivityActorType));

export function parseOptionalEntityType(value: unknown): ListSuperadminActivityEntityTypeT | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  return entityTypeValues.has(value as ListSuperadminActivityEntityTypeT)
    ? (value as ListSuperadminActivityEntityTypeT)
    : undefined;
}

export function parseOptionalActorType(value: unknown): ListSuperadminActivityActorTypeT | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  return actorTypeValues.has(value as ListSuperadminActivityActorTypeT)
    ? (value as ListSuperadminActivityActorTypeT)
    : undefined;
}

const activityFilterParamsRawSchema = z.object({
  entityType: z.string().optional(),
  entityId: z.string().optional(),
  actorType: z.string().optional(),
  actorId: z.string().optional(),
  organizationId: z.string().optional(),
  dateTimeFrom: z.string().optional(),
  dateTimeTo: z.string().optional(),
  status: z.enum(['all', 'failed']).optional().catch(undefined),
});

/** URL search params for `/superadmin/activity` (filters). */
export const ActivityFilterParamsSchema = activityFilterParamsRawSchema.transform((raw) => ({
  entityType: parseOptionalEntityType(raw.entityType),
  entityId: raw.entityId,
  actorType: parseOptionalActorType(raw.actorType),
  actorId: raw.actorId,
  organizationId: raw.organizationId,
  dateTimeFrom: raw.dateTimeFrom,
  dateTimeTo: raw.dateTimeTo,
  status: raw.status,
}));

const ENTITY_TYPE_LABELS = {
  lead: 'Company',
  contact: 'Person',
  organization: 'Organization',
  user: 'User',
  twilio_message: 'Twilio message',
  twilio_call: 'Twilio call',
  email_message: 'Email message',
  calendar_event: 'Calendar event',
  deal_stage: 'Deal stage',
  custom_field: 'Custom field',
  ai_agent: 'AI agent',
} as const satisfies Record<keyof typeof ListSuperadminActivityEntityType, string>;

/** Labels keyed by generated API enum; `value` always matches `ListSuperadminActivityEntityType`. */
export const ACTIVITY_EVENT_ENTITY_TYPE_OPTIONS = (
  Object.keys(ListSuperadminActivityEntityType) as Array<keyof typeof ListSuperadminActivityEntityType>
).map((key) => ({
  value: ListSuperadminActivityEntityType[key],
  label: ENTITY_TYPE_LABELS[key],
}));

const ACTOR_TYPE_LABELS = {
  organization_member: 'Organization member',
  agent: 'Agent',
  import: 'Import',
  system: 'System',
  k3x_support: 'K3X support',
  user: 'User',
  integration: 'Integration',
  reveal: 'Reveal',
} as const satisfies Record<keyof typeof ListSuperadminActivityActorType, string>;

/** Labels keyed by generated API enum; `value` always matches `ListSuperadminActivityActorType`. */
export const ACTOR_TYPE_OPTIONS = (
  Object.keys(ListSuperadminActivityActorType) as Array<keyof typeof ListSuperadminActivityActorType>
).map((key) => ({
  value: ListSuperadminActivityActorType[key],
  label: ACTOR_TYPE_LABELS[key],
}));
